import { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import RegistrarLabel, { DestinationBadge } from '../components/CurrentRegistrar';
import DomainHistory from '../components/DomainHistory';
import { Lock, AlertTriangle,
  ArrowLeft,
  Trash2,
  RefreshCw,
  ExternalLink,
  Calendar,
  Shield,
  Server,
  Pencil,
  Check,
  X,
  Search,
  ChevronDown,
  ChevronRight,
  Clock,
  FileText,
  DollarSign,
  ShieldAlert,
  ShoppingCart,
  Activity,
  Globe,
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';
import { useToast } from '../components/ui/Toast';
import { Domain, DomainWhois, Tag, ApiResponse, ExposureMatch, LivenessCheck, NsChange } from '../types';
import { LivenessBadge } from '../components/LivenessBadge';
import NameserverEditModal from '../components/NameserverEditModal';
import NsChangeStatus from '../components/NsChangeStatus';
import { registrarName } from '../lib/registrar-names';
import ClassificationCombobox from '../components/ClassificationCombobox';
import RenewModal from '../components/RenewModal';
import RenewalStatus from '../components/RenewalStatus';
import { format } from 'date-fns';
import GeoMultiSelect from '../components/GeoMultiSelect';
import { GeoList } from '../components/GeoFlags';
import { parseNameserverList } from '../lib/nameservers';
import { sameGeoList } from '../lib/geo';
import PageHeader from '../components/ui/PageHeader';
import EmptyState from '../components/ui/EmptyState';
import { Skeleton, SkeletonCard } from '../components/ui/Skeleton';
import MultiValueCombobox from '../components/MultiValueCombobox';
import DomainStatusBadge from '../components/DomainStatusBadge';
import TierBadge from '../components/TierBadge';
import { useConfirm } from '../components/ui/ConfirmDialog';

export default function DomainDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { fetchApi, isAdmin, isSuperAdmin, canClassify, canSeeCost, canSeeAcquisition, canEditValuation, canSeeWhois, canManageNameservers, canRenewDomains } = useApi();
  const toast = useToast();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [domain, setDomain] = useState<Domain | null>(null);
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [whoisData, setWhoisData] = useState<DomainWhois | null>(null);
  const [whoisLoading, setWhoisLoading] = useState(false);
  const [exposure, setExposure] = useState<ExposureMatch[]>([]);
  const [livenessHistory, setLivenessHistory] = useState<LivenessCheck[]>([]);
  const [livenessChecking, setLivenessChecking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [nsEditOpen, setNsEditOpen] = useState(false);
  const [nsBusy, setNsBusy] = useState(false);
  const [nsChange, setNsChange] = useState<NsChange | null>(null);
  const [renewOpen, setRenewOpen] = useState(false);
  const [renewalRefresh, setRenewalRefresh] = useState(0);
  // Existing values per classification field, for the pickers.
  const [classOptions, setClassOptions] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (!canClassify) return;
    fetchApi<ApiResponse<Record<string, string[]>>>('/reports/filter-options')
      .then((res) => { if (res.data) setClassOptions(res.data); })
      .catch(() => { /* non-fatal: picker still allows creating */ });
  }, [fetchApi, canClassify]);

  const saveClassification = async (field: string, value: string | null) => {
    await handleFieldSave(field, value ?? '');
    // A newly created value becomes an option immediately.
    if (value) {
      const added = [value];
      setClassOptions((prev) => {
        const list = prev[field] ?? [];
        const fresh = added.filter((a) => !list.some((v) => v.toLowerCase() === a.toLowerCase()));
        return fresh.length ? { ...prev, [field]: [...list, ...fresh] } : prev;
      });
    }
  };

  useEffect(() => {
    async function loadData() {
      try {
        // Load each resource independently: WHOIS/liveness are admin-only and
        // exposure/tags may 403 for read users. A single rejected fetch must not
        // block rendering the domain itself (otherwise the page falsely shows
        // "Domain not found"). Use allSettled and apply whatever succeeds.
        const [domainRes, tagsRes, whoisRes, exposureRes, livenessRes] = await Promise.allSettled([
          fetchApi<ApiResponse<Domain>>(`/domains/${id}`),
          fetchApi<ApiResponse<Tag[]>>('/tags'),
          fetchApi<ApiResponse<DomainWhois>>(`/whois/domain/${id}`),
          fetchApi<ApiResponse<ExposureMatch[]>>(`/whois/domain/${id}/exposure`),
          fetchApi<ApiResponse<LivenessCheck[]>>(`/liveness/domain/${id}/history`),
        ]);
        if (domainRes.status === 'fulfilled' && domainRes.value.data) setDomain(domainRes.value.data);
        if (tagsRes.status === 'fulfilled' && tagsRes.value.data) setAllTags(tagsRes.value.data);
        if (whoisRes.status === 'fulfilled' && whoisRes.value.data) setWhoisData(whoisRes.value.data);
        if (exposureRes.status === 'fulfilled' && exposureRes.value.data) setExposure(exposureRes.value.data);
        if (livenessRes.status === 'fulfilled' && livenessRes.value.data) setLivenessHistory(livenessRes.value.data);
      } catch (error) {
        console.error('Failed to load domain:', error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [fetchApi, id]);

  const handleLivenessCheck = async () => {
    setLivenessChecking(true);
    try {
      await fetchApi(`/liveness/domain/${id}/check`, { method: 'POST' });
      const [domainRes, livenessRes] = await Promise.all([
        fetchApi<ApiResponse<Domain>>(`/domains/${id}`),
        fetchApi<ApiResponse<LivenessCheck[]>>(`/liveness/domain/${id}/history`),
      ]);
      if (domainRes.data) setDomain(domainRes.data);
      if (livenessRes.data) setLivenessHistory(livenessRes.data);
    } catch (error) {
      console.error('Liveness check failed:', error);
      toast.error('Liveness check failed.');
    } finally {
      setLivenessChecking(false);
    }
  };

  const handleWhoisLookup = async () => {
    setWhoisLoading(true);
    try {
      const res = await fetchApi<ApiResponse<DomainWhois>>(`/whois/domain/${id}/lookup`, {
        method: 'POST',
      });
      if (res.data) setWhoisData(res.data);
      // Exposure can change after a fresh lookup — refresh it
      const exposureRes = await fetchApi<ApiResponse<ExposureMatch[]>>(`/whois/domain/${id}/exposure`);
      setExposure(exposureRes.data || []);
    } catch (error) {
      console.error('WHOIS lookup failed:', error);
      toast.error('WHOIS lookup failed', 'The domain server may be rate-limiting requests.');
    } finally {
      setWhoisLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!(await confirm({
      title: `Delete ${domain?.domain_name ?? 'this domain'}?`,
      message: 'This removes the domain from DomainHQ. It does not cancel or transfer it at the registrar.',
      tone: 'danger',
    }))) return;

    setDeleting(true);
    try {
      await fetchApi(`/domains/${id}`, { method: 'DELETE' });
      navigate('/domains');
    } catch (error) {
      toast.error('Failed to delete domain');
      setDeleting(false);
    }
  };

  const handleTagToggle = async (tagId: string, add: boolean) => {
    try {
      if (add) {
        await fetchApi(`/domains/${id}/tags`, {
          method: 'POST',
          body: JSON.stringify({ tag_ids: [tagId] }),
        });
      } else {
        await fetchApi(`/domains/${id}/tags/${tagId}`, {
          method: 'DELETE',
        });
      }
      const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
      if (res.data) setDomain(res.data);
    } catch (error) {
      console.error('Failed to update tags:', error);
    }
  };

  // Geo is a list; [] clears it. New codes become picker options immediately.
  const saveGeo = async (codes: string[]) => {
    try {
      await fetchApi(`/domains/${id}`, { method: 'PUT', body: JSON.stringify({ geo: codes }) });
      const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
      if (res.data) setDomain(res.data);
      setClassOptions((prev) => {
        const list = prev.geo ?? [];
        const fresh = codes.filter((c) => !list.includes(c));
        return fresh.length ? { ...prev, geo: [...list, ...fresh] } : prev;
      });
    } catch (error) {
      console.error('Failed to update geo:', error);
    }
  };

  // List fields (vertical) save as an array; [] clears. New values become options immediately.
  const saveList = async (field: 'vertical', values: string[]) => {
    try {
      await fetchApi(`/domains/${id}`, { method: 'PUT', body: JSON.stringify({ [field]: values }) });
      const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
      if (res.data) setDomain(res.data);
      setClassOptions((prev) => {
        const list = prev[field] ?? [];
        const fresh = values.filter((v) => !list.some((x) => x.toLowerCase() === v.toLowerCase()));
        return fresh.length ? { ...prev, [field]: [...list, ...fresh] } : prev;
      });
    } catch (error) {
      console.error(`Failed to update ${field}:`, error);
    }
  };

  const handleFieldSave = async (field: string, value: string) => {
    try {
      await fetchApi(`/domains/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ [field]: value || null }),
      });
      const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
      if (res.data) setDomain(res.data);
    } catch (error) {
      console.error('Failed to update field:', error);
    }
  };

  // Cost + currency are saved together so the two fields are always in sync.
  const handleCostSave = async (cost: number | null, currency: string | null) => {
    try {
      await fetchApi(`/domains/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ cost, cost_currency: currency }),
      });
      const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
      if (res.data) setDomain(res.data);
    } catch (error) {
      toast.error('Failed to update cost', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleAcquisitionSave = async (method: string | null) => {
    try {
      await fetchApi(`/domains/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ acquisition_method: method }),
      });
      const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
      if (res.data) setDomain(res.data);
    } catch (error) {
      toast.error('Failed to update acquisition method', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  const handleAcquisitionDateSave = async (date: string | null) => {
    try {
      await fetchApi(`/domains/${id}`, {
        method: 'PUT',
        body: JSON.stringify({ acquisition_date: date }),
      });
      const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
      if (res.data) setDomain(res.data);
    } catch (error) {
      toast.error('Failed to update acquisition date', error instanceof Error ? error.message : 'Unknown error');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-72" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6"><SkeletonCard lines={5} /><SkeletonCard lines={7} /></div>
          <SkeletonCard lines={4} />
        </div>
      </div>
    );
  }

  if (!domain) {
    return (
      <EmptyState
        icon={Globe}
        title="Domain not found"
        description="It may have been deleted, or it's outside the domains you can see."
        action={<Link to="/domains" className="btn btn-secondary">Back to domains</Link>}
      />
    );
  }

  const nameservers = parseNameserverList(domain.nameservers);

  // Server-derived: the domain's registrar has a supported NS write (Ilkari, Porkbun, Safenames, GoDaddy).
  const canEditNs = canManageNameservers && !!domain.ns_update_supported;

  const reloadDomain = async () => {
    const res = await fetchApi<ApiResponse<Domain>>(`/domains/${id}`);
    if (res.data) setDomain(res.data);
  };

  const submitNameservers = async (ns: string[]) => {
    setNsBusy(true);
    try {
      const put = await fetchApi<ApiResponse<Domain & { ns_change?: NsChange }>>(`/domains/${id}/nameservers`, { method: 'PUT', body: JSON.stringify({ nameservers: ns }) });
      if (put.data?.ns_change) setNsChange(put.data.ns_change);
      await reloadDomain();
      setNsEditOpen(false);
    } finally {
      setNsBusy(false);
    }
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <Link
          to="/domains"
          className="btn-ghost mt-1 shrink-0"
          title="Back to domains"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div className="flex-1 min-w-0">
          <PageHeader
            title={
              <span className="flex min-w-0 items-center gap-2">
                <span className="truncate">{domain.domain_name}</span>
                <a
                  href={`https://${domain.domain_name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-ghost shrink-0 text-slate-400 hover:text-brand-600"
                  title={`Visit ${domain.domain_name}`}
                  aria-label={`Visit ${domain.domain_name} in a new tab`}
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </span>
            }
            meta={
              <div className="flex flex-wrap items-center gap-3">
                <span className="text-sm text-slate-500">
                  <RegistrarLabel fallback={domain.registrar_name || domain.registrar_id} current={domain.current_registrar} />
                </span>
                <DomainStatusBadge size="sm" status={domain.status} registrarStatus={domain.registrar_status} />
                {domain.tld && (
                  <span className="text-xs font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">
                    .{domain.tld}
                  </span>
                )}
                {canSeeWhois && domain.is_exposed && (
                  <span className="inline-flex items-center gap-1 text-xs font-medium bg-red-50 text-red-700 px-2 py-0.5 rounded-full border border-red-200">
                    <ShieldAlert className="h-3.5 w-3.5" />
                    WHOIS Exposed
                  </span>
                )}
              </div>
            }
          />
        </div>
      </div>

      {/* Registrar mismatch: the account no longer holds the domain, or WHOIS says it moved. */}
      {(domain.status === 'transferred_out' || domain.status === 'transfer_failed' || domain.moved_away) && (
        <div className={`mb-6 flex items-start gap-3 rounded-xl border px-4 py-3 text-sm ${domain.current_registrar?.destination === 'third_party' ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
          <div className="text-amber-800">
            {domain.status === 'transfer_failed' ? (
              <>
                <p className="font-medium">The transfer into {domain.registrar_name || 'this registrar account'} failed.</p>
                <p className="mt-0.5 text-amber-700">
                  The account still lists it{domain.registrar_status ? ` (registrar status: ${domain.registrar_status})` : ''}, but never held it.
                  {domain.moved_away ? ` ${domain.moved_away}` : ''} Renewals and nameserver changes are disabled; retry the transfer with a valid auth code, or manage it where it's registered.
                </p>
              </>
            ) : domain.status === 'transferred_out' ? (
              <>
                <p className="font-medium">No longer held by {domain.registrar_name || 'this registrar account'}.</p>
                <p className="mt-0.5 text-amber-700">The registrar reports it as transferred out (or stopped listing it). Its expiry and nameservers here are the last values that account reported. Renewals and nameserver changes are disabled.</p>
              </>
            ) : (
              <>
                <p className="font-medium">This domain has probably moved to another registrar.</p>
                <p className="mt-0.5 text-amber-700">{domain.moved_away} {domain.registrar_name ? `It is still assigned to ${domain.registrar_name}, which keeps listing it.` : ''}</p>
              </>
            )}
            {domain.current_registrar?.destination_detail && (
              <p className={`mt-2 flex flex-wrap items-center gap-2 font-medium ${domain.current_registrar.destination === 'third_party' ? 'text-red-700' : 'text-amber-800'}`}>
                <DestinationBadge destination={domain.current_registrar.destination} />
                {domain.current_registrar.destination_detail}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main content */}
        <div className="lg:col-span-2 space-y-6">
          {/* WHOIS Exposure */}
          {canSeeWhois && exposure.length > 0 && (
            <div className="card border-red-200">
              <div className="px-6 py-4 border-b border-red-100 bg-red-50/50 rounded-t-xl">
                <h2 className="text-lg font-semibold text-red-700 flex items-center gap-2">
                  <ShieldAlert className="h-5 w-5 text-red-500" />
                  WHOIS Exposure
                </h2>
                <p className="text-sm text-red-600/80 mt-1">
                  This domain's public WHOIS record contains information matching your exposure keywords.
                </p>
              </div>
              <div className="p-4">
                <div className="flex flex-wrap gap-2">
                  {exposure.map((m, i) => (
                    <div
                      key={`${m.matched_keyword}-${m.matched_field}-${i}`}
                      className="inline-flex items-center gap-2 text-sm bg-red-50 border border-red-200 rounded-md px-2.5 py-1.5"
                    >
                      <span className="font-medium text-red-700">{m.matched_keyword}</span>
                      <span className="text-red-300">in</span>
                      <span className="text-red-600">{formatFieldName(m.matched_field)}</span>
                      {m.keyword_category && (
                        <span className="text-xs text-red-400">({m.keyword_category})</span>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-3">
                  Enable WHOIS privacy at your registrar to hide these details, then re-run the WHOIS lookup below.
                </p>
              </div>
            </div>
          )}

          {/* Liveness */}
          <div className="card">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Activity className="h-5 w-5 text-slate-400" />
                Liveness
              </h2>
              {isAdmin && (
                <button
                  onClick={handleLivenessCheck}
                  disabled={livenessChecking}
                  className="btn btn-secondary text-sm"
                  title="Probe this domain now (HTTP fetch + parking detection)"
                >
                  <RefreshCw className={`h-4 w-4 mr-1.5 ${livenessChecking ? 'animate-spin' : ''}`} />
                  {livenessChecking ? 'Checking...' : 'Check now'}
                </button>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              <InfoRow label="Status">
                <LivenessBadge status={domain.liveness_status} />
              </InfoRow>
              <InfoRow label="Last checked">
                {domain.liveness_checked_at
                  ? format(new Date(domain.liveness_checked_at), "MMM d, yyyy 'at' h:mm a")
                  : <span className="text-slate-400">Never</span>}
              </InfoRow>
              {domain.liveness_parking_host && (
                <InfoRow label="Parking host">
                  <span className="text-amber-700">{domain.liveness_parking_host}</span>
                </InfoRow>
              )}
              {domain.liveness_final_url && (
                <InfoRow label="Final URL">
                  <a href={domain.liveness_final_url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline break-all inline-flex items-center gap-1">
                    {domain.liveness_final_url}
                    <ExternalLink className="h-3 w-3 flex-shrink-0" />
                  </a>
                </InfoRow>
              )}
            </div>
            {livenessHistory.length > 0 && (
              <div className="px-6 py-3 border-t border-slate-100">
                <p className="text-xs font-medium text-slate-500 mb-2">Recent checks</p>
                <div className="space-y-1">
                  {livenessHistory.slice(0, 8).map((h) => (
                    <div key={h.id} className="flex items-center justify-between text-xs">
                      <LivenessBadge status={h.status} />
                      <span className="text-slate-400">
                        {format(new Date(h.checked_at), "MMM d, h:mm a")}
                        {h.http_status ? ` · HTTP ${h.http_status}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Registration */}
          <div className="card">
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-slate-400" />
                Registration
              </h2>
              {canRenewDomains && domain.renew_supported && (
                <button onClick={() => setRenewOpen(true)} className="btn btn-secondary text-sm" title="Renew for 1 year at the registrar (charges the registrar account)">
                  Renew…
                </button>
              )}
            </div>
            <div className="divide-y divide-slate-100">
              <InfoRow label="Status">
                <DomainStatusBadge size="sm" status={domain.status} registrarStatus={domain.registrar_status} />
              </InfoRow>
              <InfoRow label="Expiry Date">
                {domain.expiry_date
                  ? format(new Date(domain.expiry_date), 'MMMM d, yyyy')
                  : <span className="text-slate-400">Not set</span>}
              </InfoRow>
              <InfoRow label="Registration Date">
                {domain.registration_date
                  ? format(new Date(domain.registration_date), 'MMMM d, yyyy')
                  : <span className="text-slate-400">Unknown</span>}
              </InfoRow>
              <InfoRow label="Auto-Renew" icon={<RefreshCw className="h-4 w-4" />}>
                {domain.auto_renew === null ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500">Unknown</span>
                ) : domain.auto_renew ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Enabled</span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">Disabled</span>
                )}
              </InfoRow>
              <InfoRow label="WHOIS Privacy" icon={<Shield className="h-4 w-4" />}>
                {domain.whois_privacy ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">Enabled</span>
                ) : (
                  <span className="text-slate-400">Disabled</span>
                )}
              </InfoRow>
              {canSeeCost && (
                <CostRow
                  cost={domain.cost}
                  currency={domain.cost_currency}
                  canEdit={canEditValuation}
                  onSave={handleCostSave}
                />
              )}
              {canSeeAcquisition && (
                <AcquisitionMethodRow
                  value={domain.acquisition_method ?? null}
                  canEdit={canEditValuation}
                  allowOther={isSuperAdmin}
                  onSave={handleAcquisitionSave}
                />
              )}
              {canSeeAcquisition && (
                <AcquisitionDateRow
                  value={domain.acquisition_date ?? null}
                  canEdit={canEditValuation}
                  onSave={handleAcquisitionDateSave}
                />
              )}
            </div>
            {canRenewDomains && domain.renew_supported && (
              <RenewalStatus domainId={domain.id} refreshKey={renewalRefresh} onChanged={reloadDomain} />
            )}
          </div>

          {/* Classification */}
          <div className="card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Classification
              </h2>
            </div>
            <div className="divide-y divide-slate-100">
              {/* Tier comes from Cove and is read-only here (refreshed daily). */}
              <div className="flex items-center justify-between px-6 py-3">
                <dt className="flex shrink-0 items-center gap-2 text-sm text-slate-500">
                  Tier
                  <span className="inline-flex items-center gap-1 text-xs text-slate-400" title={domain.tier_synced_at ? `Synced from Cove ${new Date(domain.tier_synced_at).toLocaleString()} — read-only here` : 'Set in Cove; DomainHQ syncs it daily (read-only here)'}>
                    <Lock className="h-3 w-3" /> Source: Cove
                  </span>
                </dt>
                <dd className="text-sm">
                  {domain.tier ? <TierBadge tier={domain.tier} /> : <span className="text-slate-400">-</span>}
                </dd>
              </div>
              <ClassificationRow
                label="Type"
                value={domain.domain_type}
                field="domain_type"
                options={classOptions.domain_type ?? []}
                canEdit={canClassify}
                canCreate={isAdmin}
                onSave={saveClassification}
              />
              <ListClassificationRow
                label="Vertical"
                value={domain.vertical ?? []}
                options={classOptions.vertical ?? []}
                canEdit={canClassify}
                canCreate={isAdmin}
                onSave={(values) => saveList('vertical', values)}
              />
              <ClassificationRow
                label="Niche"
                value={domain.niche}
                field="niche"
                options={classOptions.niche ?? []}
                canEdit={canClassify}
                canCreate={isAdmin}
                onSave={saveClassification}
              />
              <ClassificationRow
                label="Sub-Niche"
                value={domain.subniche}
                field="subniche"
                options={classOptions.subniche ?? []}
                canEdit={canClassify}
                canCreate={isAdmin}
                onSave={saveClassification}
              />
              <GeoRow
                value={domain.geo}
                coveGeo={domain.cove_geo ?? []}
                mismatch={!!domain.geo_mismatch}
                locked={!!domain.geo_locked}
                options={classOptions.geo ?? []}
                canEdit={canClassify}
                canCreate={isAdmin}
                onSave={saveGeo}
              />
              <ClassificationRow
                label="Cloaking Type"
                value={domain.cloaking_type}
                field="cloaking_type"
                options={classOptions.cloaking_type ?? []}
                canEdit={canClassify}
                canCreate={isAdmin}
                onSave={saveClassification}
              />
              <ClassificationRow
                label="Subreddit"
                value={domain.subreddit}
                field="subreddit"
                options={classOptions.subreddit ?? []}
                canEdit={canClassify}
                canCreate={isAdmin}
                onSave={saveClassification}
              />
            </div>
          </div>

          {/* Notes */}
          <div className="card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="h-5 w-5 text-slate-400" />
                Notes
              </h2>
            </div>
            <EditableTextArea
              value={domain.notes}
              field="notes"
              placeholder="Add notes about this domain..."
              canEdit={canClassify}
              appendOnly={canClassify && !isAdmin}
              onSave={handleFieldSave}
            />
          </div>

          {/* Nameservers */}
          <NameserversSection
            registrarNameservers={nameservers}
            whoisData={whoisData}
            editable={canEditNs}
            onEdit={() => setNsEditOpen(true)}
          />
          {canEditNs && (
            <NsChangeStatus domainId={domain.id} change={nsChange} onResolved={reloadDomain} />
          )}
          {renewOpen && (
            <RenewModal
              domainIds={[domain.id]}
              onClose={() => setRenewOpen(false)}
              onDone={() => { reloadDomain(); setRenewalRefresh((n) => n + 1); }}
            />
          )}
          {nsEditOpen && (
            <NameserverEditModal
              title={`Edit nameservers — ${domain.domain_name}`}
              initial={nameservers}
              notice={`Pushed to ${registrarName(domain.registrar_type)}.`}
              busy={nsBusy}
              onClose={() => setNsEditOpen(false)}
              onSubmit={submitNameservers}
            />
          )}

          {/* WHOIS — only for users granted WHOIS visibility */}
          {canSeeWhois && (
            <WhoisSection
              whoisData={whoisData}
              onLookup={handleWhoisLookup}
              loading={whoisLoading}
              isAdmin={isAdmin}
            />
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Tags */}
          <div className="card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">Tags</h2>
            </div>
            <div className="p-4">
              <div className="space-y-1.5">
                {allTags.map((tag) => {
                  const isSelected = domain.tags?.some((t) => t.id === tag.id);
                  return (
                    <label
                      key={tag.id}
                      className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer border transition-colors ${
                        isSelected
                          ? 'border-brand-300 bg-brand-50'
                          : 'border-transparent hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) =>
                          handleTagToggle(tag.id, e.target.checked)
                        }
                        disabled={!isAdmin}
                        className="rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                      />
                      <span
                        className="w-2.5 h-2.5 rounded-full shrink-0"
                        style={{ backgroundColor: tag.color }}
                      />
                      <span className="text-sm text-slate-700">{tag.name}</span>
                    </label>
                  );
                })}
              </div>
              {allTags.length === 0 && (
                <p className="text-slate-400 text-sm">
                  No tags available.{' '}
                  <Link to="/tags" className="text-brand-600">
                    Create one
                  </Link>
                </p>
              )}
            </div>
          </div>

          {/* System */}
          <div className="card">
            <div className="px-6 py-4 border-b border-slate-200">
              <h2 className="text-sm font-semibold text-slate-500 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                System
              </h2>
            </div>
            <div className="p-4 space-y-2.5 text-xs">
              <div className="flex justify-between">
                <span className="text-slate-400">Source</span>
                <span className="text-slate-600">{{ 'manual': 'Manual', 'csv_import': 'CSV Import', 'registrar-api': 'Registrar API' }[domain.source] || domain.source}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Last Synced</span>
                <span className="text-slate-600">
                  {domain.last_synced
                    ? format(new Date(domain.last_synced), 'MMM d, yyyy h:mm a')
                    : 'Never'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Created</span>
                <span className="text-slate-600">{format(new Date(domain.created_at), 'MMM d, yyyy')}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-400">Updated</span>
                <span className="text-slate-600">{format(new Date(domain.updated_at), 'MMM d, yyyy')}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <DomainHistory domainId={domain.id} />

      {/* Rarely needed, so tucked away at the bottom and collapsed. */}
      {isAdmin && (
        <details className="group mt-8 rounded-xl border border-slate-200">
          <summary className="flex cursor-pointer list-none items-center gap-2 px-5 py-3 text-sm font-medium text-slate-500 hover:text-slate-700">
            <ChevronRight className="h-4 w-4 transition-transform group-open:rotate-90" />
            Danger zone
          </summary>
          <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 px-5 py-4">
            <div>
              <p className="text-sm font-medium text-slate-900">Delete this domain from DomainHQ</p>
              <p className="text-sm text-slate-500">Removes it from the inventory only — nothing changes at the registrar. A registrar sync will add it back if the account still lists it.</p>
            </div>
            <button onClick={handleDelete} disabled={deleting} className="btn btn-danger">
              <Trash2 className="mr-2 h-4 w-4" />
              {deleting ? 'Deleting…' : 'Delete domain'}
            </button>
          </div>
        </details>
      )}
    </div>
  );
}

// Turns a WHOIS field key like "registrant_email" into "Registrant Email"
function formatFieldName(field: string): string {
  return field
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function InfoRow({
  label,
  icon,
  children,
}: {
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between px-6 py-3">
      <dt className="text-sm text-slate-500 flex items-center gap-1.5">
        {icon}
        {label}
      </dt>
      <dd className="text-sm text-slate-900">{children}</dd>
    </div>
  );
}

/**
 * A classification field (type, vertical, niche…). Edits go through a
 * searchable picker of existing values so near-duplicates ("home & garden"
 * vs "Home & Garden") aren't created by typing; new values need an explicit
 * Create.
 */
function ClassificationRow({
  label,
  value,
  field,
  options,
  canEdit,
  canCreate,
  onSave,
}: {
  label: string;
  value: string | null;
  field: string;
  options: string[];
  canEdit: boolean;
  /** Admins only — mirrors the server rule for new classification values. */
  canCreate: boolean;
  onSave: (field: string, value: string | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const cancel = useCallback(() => setEditing(false), []);

  const commit = async (next: string | null) => {
    if (next === value) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(field, next); } finally { setSaving(false); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 px-6 py-2.5">
        <dt className="text-sm text-slate-500 w-28 shrink-0">{label}</dt>
        <dd className="flex-1 flex items-center">
          <ClassificationCombobox value={value} options={options} canCreate={canCreate} saving={saving} onCommit={commit} onCancel={cancel} />
        </dd>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between px-6 py-3">
      <dt className="text-sm text-slate-500 w-28 shrink-0">{label}</dt>
      <dd className="flex items-center gap-2 text-sm text-slate-900 min-w-0">
        <span className={`truncate ${!value ? 'text-slate-400' : ''}`}>{value || '-'}</span>
        {canEdit && (
          <button onClick={() => setEditing(true)} className="btn-ghost opacity-0 group-hover:opacity-100 shrink-0" title={`Edit ${label}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </dd>
    </div>
  );
}

/** A multi-value classification field (vertical): chips when viewing, MultiValueCombobox when editing. */
function ListClassificationRow({
  label,
  value,
  options,
  canEdit,
  canCreate,
  onSave,
}: {
  label: string;
  value: string[];
  options: string[];
  canEdit: boolean;
  canCreate: boolean;
  onSave: (values: string[]) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const cancel = useCallback(() => setEditing(false), []);

  const commit = async (next: string[]) => {
    const same = next.length === value.length && next.every((v) => value.some((x) => x.toLowerCase() === v.toLowerCase()));
    if (same) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(next); } finally { setSaving(false); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="flex items-start gap-2 px-6 py-2.5">
        <dt className="w-28 shrink-0 pt-1.5 text-sm text-slate-500">{label}</dt>
        <dd className="flex flex-1 items-center">
          <MultiValueCombobox value={value} options={options} canCreate={canCreate} saving={saving} onCommit={commit} onCancel={cancel} />
        </dd>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between px-6 py-3">
      <dt className="w-28 shrink-0 text-sm text-slate-500">{label}</dt>
      <dd className="flex min-w-0 items-center gap-2 text-sm text-slate-900">
        {value.length
          ? <span className="inline-flex flex-wrap justify-end gap-1">{value.map((v) => <span key={v} className="rounded bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-700">{v}</span>)}</span>
          : <span className="text-slate-400">-</span>}
        {canEdit && (
          <button onClick={() => setEditing(true)} className="btn-ghost shrink-0 opacity-0 group-hover:opacity-100" title={`Edit ${label}`}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </dd>
    </div>
  );
}

function GeoRow({
  value,
  coveGeo,
  mismatch,
  locked,
  options,
  canEdit,
  canCreate,
  onSave,
}: {
  value: string[];
  /** Cove's target geos for this site (read-only), [] when Cove has none. */
  coveGeo: string[];
  mismatch: boolean;
  /** o&o sites: Geo comes from Cove and can't be edited here. */
  locked: boolean;
  options: string[];
  canEdit: boolean;
  canCreate: boolean;
  onSave: (codes: string[]) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const cancel = useCallback(() => setEditing(false), []);

  const commit = async (next: string[]) => {
    if (sameGeoList(next, value)) { setEditing(false); return; }
    setSaving(true);
    try { await onSave(next); } finally { setSaving(false); setEditing(false); }
  };

  if (editing) {
    return (
      <div className="flex items-start gap-2 px-6 py-2.5">
        <dt className="text-sm text-slate-500 w-28 shrink-0 pt-1.5">Geo</dt>
        <dd className="flex-1 flex items-center">
          <GeoMultiSelect value={value} options={options} canCreate={canCreate} saving={saving} onCommit={commit} onCancel={cancel} />
        </dd>
      </div>
    );
  }

  return (
    <div className="group px-6 py-3">
    <div className="flex items-center justify-between">
      <dt className="flex shrink-0 items-center gap-2 text-sm text-slate-500">
        Geo
        {/* Cove supplies the target geos. o&o sites: read-only (padlock). Other types: editable, so no padlock. */}
        {coveGeo.length > 0 && !mismatch && (
          <span className="inline-flex items-center gap-1 text-xs text-slate-400"
            title={locked ? 'o&o site: Geo comes from Cove (synced daily) and is read-only here. Change it in Cove.' : 'Target geos from Cove (synced daily). Editing here makes it a mismatch with Cove.'}>
            {locked && <Lock className="h-3 w-3" />} Source: Cove
          </span>
        )}
      </dt>
      <dd className="flex items-center gap-2 text-sm text-slate-900 min-w-0">
        <GeoList value={value} max={8} />
        {canEdit && !locked && (
          <button onClick={() => setEditing(true)} className="btn-ghost opacity-0 group-hover:opacity-100 shrink-0" title="Edit Geo">
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </dd>
    </div>
    {/* Cove is the source of target geos for tiered sites; a different value here is flagged, not overwritten. */}
    {mismatch ? (
      <div className="mt-2 flex flex-wrap items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
        <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-600" />
        <span>Cove targets</span>
        <GeoList value={coveGeo} max={8} />
        <span>— different from DomainHQ.</span>
        {canEdit && (
          <button type="button" disabled={saving} onClick={() => commit(coveGeo)} className="ml-auto rounded border border-amber-300 bg-white/60 px-2 py-0.5 font-medium text-amber-800 hover:bg-white">
            Use Cove's geo
          </button>
        )}
      </div>
    ) : null}
    </div>
  );
}

// Common ISO 4217 currencies. Not exhaustive — covers ~99% of domain
// payments. Anything else can be entered via the API directly; the input
// also accepts manual codes via free-text entry while editing.
const COMMON_CURRENCIES = ['USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'CHF', 'CNY', 'INR', 'MXN', 'BRL', 'ZAR', 'NZD', 'SGD', 'HKD'];

function formatCost(cost: number | null, currency: string | null): string {
  if (cost === null || cost === undefined) return '—';
  if (currency) {
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(cost);
    } catch { /* invalid code, fall through to plain rendering */ }
    return `${cost.toFixed(2)} ${currency}`;
  }
  return cost.toFixed(2);
}

function CostRow({
  cost,
  currency,
  canEdit,
  onSave,
}: {
  cost: number | null;
  currency: string | null;
  canEdit: boolean;
  onSave: (cost: number | null, currency: string | null) => Promise<void>;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [draftCost, setDraftCost] = useState<string>(cost != null ? String(cost) : '');
  const [draftCurrency, setDraftCurrency] = useState<string>(currency || 'USD');
  const [saving, setSaving] = useState(false);
  // Cost is blurred until deliberately revealed, so it isn't readable over a
  // shoulder or in a screen share just because the page is open. Resets on
  // remount (i.e. navigating to another domain).
  const [revealed, setRevealed] = useState(false);

  const startEdit = () => {
    setDraftCost(cost != null ? String(cost) : '');
    setDraftCurrency(currency || 'USD');
    setEditing(true);
  };

  const handleSave = async () => {
    const trimmed = draftCost.trim();
    let nextCost: number | null = null;
    if (trimmed !== '') {
      const n = Number(trimmed);
      if (!isFinite(n) || n < 0) {
        toast.error('Cost must be a non-negative number');
        return;
      }
      nextCost = n;
    }
    const nextCurrency = nextCost === null ? null : (draftCurrency.trim().toUpperCase() || null);
    setSaving(true);
    await onSave(nextCost, nextCurrency);
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 px-6 py-2.5">
        <dt className="text-sm text-slate-500 flex items-center gap-1.5 w-28 shrink-0">
          <DollarSign className="h-4 w-4" />
          Cost
        </dt>
        <dd className="flex-1 flex items-center gap-1.5">
          <select
            value={draftCurrency}
            onChange={(e) => setDraftCurrency(e.target.value)}
            disabled={saving}
            className="input-sm w-[90px]"
            aria-label="Currency"
          >
            {COMMON_CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <input
            type="number"
            step="0.01"
            min="0"
            value={draftCost}
            onChange={(e) => setDraftCost(e.target.value)}
            placeholder="0.00"
            autoFocus
            className="input-sm flex-1 max-w-[140px]"
            disabled={saving}
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-ghost text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="btn-ghost text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between px-6 py-3">
      <dt className="text-sm text-slate-500 flex items-center gap-1.5">
        <DollarSign className="h-4 w-4" />
        Cost
      </dt>
      <dd className="text-sm flex items-center gap-2">
        {cost == null ? (
          <span className="text-slate-400">{formatCost(cost, currency)}</span>
        ) : (
          <button
            type="button"
            onClick={() => setRevealed((v) => !v)}
            title={revealed ? 'Hide cost' : 'Click to reveal cost'}
            aria-label={revealed ? 'Hide cost' : 'Reveal cost'}
            aria-pressed={revealed}
            className={`text-slate-900 rounded transition-[filter] duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
              revealed ? '' : 'blur-[5px] select-none cursor-pointer'
            }`}
          >
            {formatCost(cost, currency)}
          </button>
        )}
        {canEdit && (
          <button
            onClick={startEdit}
            className="btn-ghost opacity-0 group-hover:opacity-100 shrink-0"
            title="Edit cost"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </dd>
    </div>
  );
}

// Predefined acquisition methods offered in the dropdown. "Other" reveals a
// free-text input so any value can still be recorded.
const ACQUISITION_METHODS = [
  'Auction',
  'Backorder / Drop-catch',
  'Direct purchase',
  'Broker / Negotiated',
  'Hand-registered',
  'Acquired with company',
  'Marketplace',
];

function AcquisitionMethodRow({
  value,
  canEdit,
  allowOther,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  /** Only super admins may set a free-text "Other…" value. */
  allowOther: boolean;
  onSave: (method: string | null) => Promise<void>;
}) {
  const isPreset = (v: string | null) => v != null && ACQUISITION_METHODS.includes(v);
  // A non-preset existing value can only have been set by a super admin.
  // Non-super-admins can keep it (shown as '__current__') but can't create one.
  const isCustom = (v: string | null) => v != null && v !== '' && !isPreset(v);
  const initialSelect = (v: string | null) =>
    v == null || v === '' ? '' : isPreset(v) ? v : allowOther ? '__other__' : '__current__';
  const [editing, setEditing] = useState(false);
  const [draftSelect, setDraftSelect] = useState<string>(initialSelect(value));
  const [draftOther, setDraftOther] = useState<string>(isPreset(value) ? '' : value || '');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraftSelect(initialSelect(value));
    setDraftOther(isPreset(value) ? '' : value || '');
    setEditing(true);
  };

  const handleSave = async () => {
    let next: string | null;
    if (draftSelect === '') {
      next = null;
    } else if (draftSelect === '__other__') {
      next = draftOther.trim() || null;
    } else if (draftSelect === '__current__') {
      next = value; // preserve the existing custom value unchanged
    } else {
      next = draftSelect;
    }
    setSaving(true);
    await onSave(next);
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 px-6 py-2.5">
        <dt className="text-sm text-slate-500 flex items-center gap-1.5 w-28 shrink-0">
          <ShoppingCart className="h-4 w-4" />
          Acquisition
        </dt>
        <dd className="flex-1 flex items-center gap-1.5">
          <select
            value={draftSelect}
            onChange={(e) => setDraftSelect(e.target.value)}
            disabled={saving}
            autoFocus
            className="input-sm flex-1 max-w-[200px]"
          >
            <option value="">— Not set —</option>
            {ACQUISITION_METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
            {allowOther && <option value="__other__">Other…</option>}
            {!allowOther && isCustom(value) && <option value="__current__">{value}</option>}
          </select>
          {allowOther && draftSelect === '__other__' && (
            <input
              type="text"
              value={draftOther}
              onChange={(e) => setDraftOther(e.target.value)}
              placeholder="Describe…"
              maxLength={200}
              className="input-sm flex-1 max-w-[180px]"
              disabled={saving}
            />
          )}
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-ghost text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="btn-ghost text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between px-6 py-3">
      <dt className="text-sm text-slate-500 flex items-center gap-1.5">
        <ShoppingCart className="h-4 w-4" />
        Acquisition
      </dt>
      <dd className="text-sm flex items-center gap-2">
        <span className={value == null ? 'text-slate-400' : 'text-slate-900'}>
          {value || '—'}
        </span>
        {canEdit && (
          <button
            onClick={startEdit}
            className="btn-ghost opacity-0 group-hover:opacity-100 shrink-0"
            title="Edit acquisition method"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </dd>
    </div>
  );
}

function AcquisitionDateRow({
  value,
  canEdit,
  onSave,
}: {
  value: string | null;
  canEdit: boolean;
  onSave: (date: string | null) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<string>(value || '');
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setDraft(value || '');
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    await onSave(draft.trim() === '' ? null : draft);
    setSaving(false);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="flex items-center gap-2 px-6 py-2.5">
        <dt className="text-sm text-slate-500 flex items-center gap-1.5 w-28 shrink-0">
          <Calendar className="h-4 w-4" />
          Acquired
        </dt>
        <dd className="flex-1 flex items-center gap-1.5">
          <input
            type="date"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            autoFocus
            disabled={saving}
            className="input-sm max-w-[170px]"
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-ghost text-green-600 hover:text-green-700 hover:bg-green-50"
          >
            <Check className="h-4 w-4" />
          </button>
          <button
            onClick={() => setEditing(false)}
            className="btn-ghost text-slate-400 hover:text-slate-600"
          >
            <X className="h-4 w-4" />
          </button>
        </dd>
      </div>
    );
  }

  return (
    <div className="group flex items-center justify-between px-6 py-3">
      <dt className="text-sm text-slate-500 flex items-center gap-1.5">
        <Calendar className="h-4 w-4" />
        Acquired
      </dt>
      <dd className="text-sm flex items-center gap-2">
        <span className={value == null ? 'text-slate-400' : 'text-slate-900'}>
          {value ? format(new Date(`${value}T00:00:00`), 'MMM d, yyyy') : '—'}
        </span>
        {canEdit && (
          <button
            onClick={startEdit}
            className="btn-ghost opacity-0 group-hover:opacity-100 shrink-0"
            title="Edit acquisition date"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </dd>
    </div>
  );
}

function EditableTextArea({
  value,
  field,
  placeholder,
  canEdit,
  appendOnly,
  onSave,
}: {
  value: string | null;
  field: string;
  placeholder?: string;
  canEdit: boolean;
  appendOnly?: boolean;
  onSave: (field: string, value: string) => Promise<void>;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const startEditing = () => {
    setDraft(appendOnly ? '' : (value || ''));
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    if (appendOnly && value) {
      // Append new text after existing content
      const appended = draft.trim()
        ? value + '\n' + draft
        : value;
      await onSave(field, appended);
    } else {
      await onSave(field, draft);
    }
    setSaving(false);
    setEditing(false);
  };

  const handleCancel = () => {
    setDraft('');
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="px-6 py-4">
        <div className="flex items-center justify-between mb-2">
          {appendOnly && value ? (
            <span className="text-xs text-slate-400">Appending to existing notes</span>
          ) : (
            <span />
          )}
          <div className="flex gap-1">
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-ghost text-green-600 hover:text-green-700 hover:bg-green-50"
            >
              <Check className="h-4 w-4" />
            </button>
            <button
              onClick={handleCancel}
              className="btn-ghost text-slate-400 hover:text-slate-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {appendOnly && value && (
          <div className="text-sm text-slate-500 whitespace-pre-wrap mb-2 pb-2 border-b border-slate-100">
            {value}
          </div>
        )}
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={appendOnly ? 'Add to notes...' : placeholder}
          autoFocus
          rows={3}
          className="input w-full text-sm resize-y"
          disabled={saving}
        />
      </div>
    );
  }

  return (
    <div
      className={`group px-6 py-4 ${canEdit ? 'cursor-pointer hover:bg-slate-50 transition-colors' : ''}`}
      onClick={canEdit ? startEditing : undefined}
    >
      <dd className={`text-sm whitespace-pre-wrap ${value ? 'text-slate-700' : 'text-slate-400'}`}>
        {value || placeholder || '-'}
      </dd>
    </div>
  );
}

function NameserversSection({
  registrarNameservers,
  whoisData,
  editable,
  onEdit,
}: {
  registrarNameservers: string[];
  whoisData: DomainWhois | null;
  editable?: boolean;
  onEdit?: () => void;
}) {
  const whoisNameservers = parseNameserverList(whoisData?.whois_nameservers);

  // Normalize for comparison (lowercase, trim)
  const normalize = (ns: string) => ns.toLowerCase().replace(/\.$/, '').trim();
  const registrarSet = new Set(registrarNameservers.map(normalize));
  const whoisSet = new Set(whoisNameservers.map(normalize));
  const match = registrarSet.size > 0 && whoisSet.size > 0 &&
    registrarSet.size === whoisSet.size &&
    [...registrarSet].every((ns) => whoisSet.has(ns));

  const hasRegistrar = registrarNameservers.length > 0;
  const hasWhois = whoisNameservers.length > 0;
  const showBoth = hasRegistrar && hasWhois && !match;

  if (!hasRegistrar && !hasWhois) {
    return (
      <div className="card">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Server className="h-5 w-5 text-slate-400" />
            Nameservers
          </h2>
          {editable && (
            <button onClick={onEdit} className="btn btn-secondary text-sm" title="Edit nameservers at the registrar">
              Edit
            </button>
          )}
        </div>
        <div className="p-6">
          <p className="text-slate-400 text-sm">No nameservers configured</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Server className="h-5 w-5 text-slate-400" />
          Nameservers
        </h2>
        {editable && (
          <button onClick={onEdit} className="btn btn-secondary text-sm" title="Edit nameservers at the registrar">
            Edit
          </button>
        )}
      </div>
      <div className="p-6">
        {showBoth ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">Registrar</span>
                <span className="text-xs text-slate-400">(configured)</span>
              </div>
              <NameserverList nameservers={registrarNameservers} />
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wider">WHOIS</span>
                <span className="text-xs text-amber-500">(differs)</span>
              </div>
              <NameserverList nameservers={whoisNameservers} />
            </div>
          </div>
        ) : (
          <>
            <NameserverList nameservers={hasRegistrar ? registrarNameservers : whoisNameservers} />
            <div className="mt-2 text-xs text-slate-400">
              Source: {hasRegistrar ? 'Registrar API' : 'WHOIS lookup'}
              {hasRegistrar && hasWhois && match && ' (matches WHOIS)'}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function NameserverList({ nameservers }: { nameservers: string[] }) {
  return (
    <div className="space-y-1.5">
      {nameservers.map((ns, i) => (
        <div
          key={i}
          className="flex items-center gap-2 text-slate-700 font-mono text-sm"
        >
          <span className="w-5 h-5 flex items-center justify-center bg-slate-100 rounded text-xs text-slate-500 shrink-0">
            {i + 1}
          </span>
          {ns}
        </div>
      ))}
    </div>
  );
}

function WhoisSection({
  whoisData,
  onLookup,
  loading,
  isAdmin,
}: {
  whoisData: DomainWhois | null;
  onLookup: () => void;
  loading: boolean;
  isAdmin: boolean;
}) {
  const [rawExpanded, setRawExpanded] = useState(false);

  const statusCodes: string[] = whoisData?.status_codes
    ? (() => { try { return JSON.parse(whoisData.status_codes); } catch { return []; } })()
    : [];

  return (
    <div className="card">
      <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Search className="h-5 w-5 text-slate-400" />
          WHOIS
          {whoisData?.whois_source && (
            <span
              className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-md border ${
                whoisData.whois_source === 'whoxy'
                  ? 'text-violet-700 bg-violet-50 border-violet-200'
                  : 'text-slate-600 bg-slate-50 border-slate-200'
              }`}
              title="WHOIS provider that answered this lookup"
            >
              via {whoisData.whois_source === 'whoxy' ? 'Whoxy' : 'whoiser'}
            </span>
          )}
        </h2>
        {isAdmin && (
          <button
            onClick={onLookup}
            disabled={loading}
            className="btn btn-secondary text-sm"
          >
            {loading ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-1" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-1" />
            )}
            {whoisData ? 'Refresh' : 'Fetch WHOIS'}
          </button>
        )}
      </div>

      {!whoisData ? (
        <div className="p-6 text-center">
          <p className="text-slate-400 text-sm">
            No WHOIS data fetched yet.
            {isAdmin && ' Click "Fetch WHOIS" to look it up.'}
          </p>
        </div>
      ) : (
        <div className="divide-y divide-slate-100">
          {/* Dates */}
          <div className="px-6 py-3">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Dates</div>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-slate-400">Created</span>
                <div className="text-slate-700">{whoisData.whois_created_date || '-'}</div>
              </div>
              <div>
                <span className="text-slate-400">Updated</span>
                <div className="text-slate-700">{whoisData.whois_updated_date || '-'}</div>
              </div>
              <div>
                <span className="text-slate-400">Expires</span>
                <div className="text-slate-700">{whoisData.whois_expiry_date || '-'}</div>
              </div>
            </div>
          </div>

          {/* Registrar */}
          {whoisData.whois_registrar && (
            <div className="px-6 py-3">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">WHOIS Registrar</div>
              <div className="text-sm text-slate-700">{whoisData.whois_registrar}</div>
              {whoisData.whois_registrar_url && (
                <div className="text-xs text-slate-400">{whoisData.whois_registrar_url}</div>
              )}
            </div>
          )}

          {/* Contacts */}
          <div className="px-6 py-3">
            <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Contacts</div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ContactBlock
                label="Registrant"
                name={whoisData.registrant_name}
                org={whoisData.registrant_organization}
                email={whoisData.registrant_email}
                phone={whoisData.registrant_phone}
                location={[whoisData.registrant_city, whoisData.registrant_state, whoisData.registrant_country].filter(Boolean).join(', ') || null}
              />
              <ContactBlock
                label="Admin"
                name={whoisData.admin_name}
                org={whoisData.admin_organization}
                email={whoisData.admin_email}
                phone={whoisData.admin_phone}
              />
              <ContactBlock
                label="Tech"
                name={whoisData.tech_name}
                org={whoisData.tech_organization}
                email={whoisData.tech_email}
                phone={whoisData.tech_phone}
              />
            </div>
          </div>

          {/* Status Codes */}
          {statusCodes.length > 0 && (
            <div className="px-6 py-3">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">Status Codes</div>
              <div className="flex flex-wrap gap-1.5">
                {statusCodes.map((code, i) => (
                  <span key={i} className="text-xs bg-blue-50 text-blue-600 px-2 py-0.5 rounded">
                    {code.split(' ')[0]}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* DNSSEC */}
          {whoisData.dnssec && (
            <div className="px-6 py-3">
              <div className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">DNSSEC</div>
              <div className="text-sm text-slate-700">{whoisData.dnssec}</div>
            </div>
          )}

          {/* Raw WHOIS */}
          {whoisData.raw_whois && (
            <div className="px-6 py-3">
              <button
                onClick={() => setRawExpanded(!rawExpanded)}
                className="flex items-center gap-1 text-xs font-medium text-slate-500 uppercase tracking-wider hover:text-slate-700"
              >
                {rawExpanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                Raw WHOIS
              </button>
              {rawExpanded && (
                <pre className="mt-2 text-xs text-slate-600 bg-slate-50 rounded-lg p-4 overflow-x-auto max-h-96 overflow-y-auto whitespace-pre-wrap">
                  {whoisData.raw_whois}
                </pre>
              )}
            </div>
          )}

          {/* Last fetched */}
          <div className="px-6 py-2 text-xs text-slate-400">
            Last fetched: {whoisData.last_fetched ? format(new Date(whoisData.last_fetched), 'MMM d, yyyy h:mm a') : 'Unknown'}
          </div>
        </div>
      )}
    </div>
  );
}

function ContactBlock({
  label,
  name,
  org,
  email,
  phone,
  location,
}: {
  label: string;
  name: string | null;
  org: string | null;
  email: string | null;
  phone: string | null;
  location?: string | null;
}) {
  if (!name && !org && !email && !phone) {
    return (
      <div>
        <div className="text-xs font-medium text-slate-400 mb-1">{label}</div>
        <div className="text-sm text-slate-300 italic">Not available</div>
      </div>
    );
  }

  return (
    // min-w-0 + break-anywhere: long privacy-service emails wrap instead of
    // spilling into the next column.
    <div className="min-w-0 [overflow-wrap:anywhere]">
      <div className="text-xs font-medium text-slate-400 mb-1">{label}</div>
      {name && <div className="text-sm text-slate-700">{name}</div>}
      {org && <div className="text-sm text-slate-500">{org}</div>}
      {email && <div className="text-sm text-slate-500">{email}</div>}
      {phone && <div className="text-sm text-slate-500">{phone}</div>}
      {location && <div className="text-xs text-slate-400">{location}</div>}
    </div>
  );
}

